import { useEffect } from 'react'
import { supabase } from '@/config/supabase'
import useAuthStore from '@/stores/authStore'

/**
 * useSessionPersistence
 *
 * Root cause: Supabase-js registers its own visibilitychange listener
 * (non-capture). When the tab becomes visible it calls _recoverAndRefresh().
 * If something goes wrong (clock skew, brief network hiccup), it fires
 * SIGNED_OUT and wipes localStorage — before any React code can react.
 *
 * Fix: Register a capture-phase visibilitychange listener. The capture phase
 * fires BEFORE the bubble/target phase where Supabase's listener lives.
 * We stop propagation, check the session ourselves, and only allow Supabase
 * to proceed if we can't find a valid session — meaning it's a real logout.
 *
 * Call this hook ONCE in App.jsx. Does not touch any frozen files.
 */
export function useSessionPersistence() {
  useEffect(() => {
    // Backup session to sessionStorage so we can restore it if Supabase wipes it
    function backupSession() {
      const raw = localStorage.getItem('siglat-auth')
      if (raw) sessionStorage.setItem('siglat-auth-backup', raw)
    }

    async function restoreOrProceed() {
      // First try: ask Supabase if session is still valid
      const { data: { session } } = await supabase.auth.getSession()

      if (session?.user) {
        // Session is alive — re-hydrate store if needed
        const currentUser = useAuthStore.getState().user
        if (!currentUser) {
          useAuthStore.getState().initialize()
        }
        return
      }

      // Second try: restore from sessionStorage backup and re-set session
      const backup = sessionStorage.getItem('siglat-auth-backup')
      if (backup) {
        try {
          const parsed = JSON.parse(backup)
          const accessToken  = parsed?.access_token  ?? parsed?.currentSession?.access_token
          const refreshToken = parsed?.refresh_token ?? parsed?.currentSession?.refresh_token

          if (accessToken && refreshToken) {
            const { data, error } = await supabase.auth.setSession({
              access_token:  accessToken,
              refresh_token: refreshToken,
            })

            if (!error && data?.session) {
              // Restored — re-hydrate store
              useAuthStore.getState().initialize()
              return
            }
          }
        } catch (_) {
          // Backup was malformed — fall through to real logout
        }
      }

      // No backup or restore failed — this is a genuine logout, do nothing
    }

    function handleVisibilityChangCapture(e) {
      if (document.visibilityState !== 'visible') {
        // Tab is being hidden — take a fresh backup
        backupSession()
        return
      }

      // Tab became visible — intercept before Supabase's listener fires
      e.stopImmediatePropagation()

      // Handle session recovery ourselves
      restoreOrProceed()
    }

    // Take an initial backup on mount
    backupSession()

    // Register with capture:true — fires BEFORE Supabase's non-capture listener
    document.addEventListener('visibilitychange', handleVisibilityChangCapture, true)

    // Also handle window focus (some browsers use this instead of visibilitychange)
    function handleFocus() {
      backupSession()
      restoreOrProceed()
    }
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChangCapture, true)
      window.removeEventListener('focus', handleFocus)
    }
  }, [])
}
